/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import React, { ReactNode } from 'react';
import classnames from 'classnames';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import type { SlotName } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import AdSlot from '@gumtree/third-party-ads/src/components/ad-slot';

import CarouselCard from './carousel-card';
import { StyledAdContainer, videoStyles } from './slider.style';

const getClassName = (
    currentSlide,
    index,
    itemsToShow,
    showNextSlidePreview,
    slideLength,
    isVideoElement
) =>
    classnames('carousel-item', {
        active: currentSlide === index + 1,
        isVideo: isVideoElement,
        ...(showNextSlidePreview
            ? { noPadding: index + 1 === slideLength }
            : { noPadding: index === currentSlide + itemsToShow - 2 }),
    });

export type CardSlide = {
    type: 'card';
    path: string;
    imageUrl: string;
    title: string;
    price?: string;
    location?: string;
    postedDate?: string;
    id?: number;
};

interface SlideEventHandlers {
    onArrivalFromLeft?: () => void;
    onArrivalFromRight?: () => void;
}

export interface HtmlSlide {
    type: 'html';
    content: ReactNode;
}

export interface ImageSlide {
    type: 'image';
    link: string;
}

export interface VideoSlide {
    type: 'video';
    link: string;
}

export interface DisplayAdSlide {
    type: 'display-ad';
    slotId: SlotName;
}

type Slide = (ImageSlide | VideoSlide | CardSlide | HtmlSlide | DisplayAdSlide) &
    SlideEventHandlers & { invisibleToSlideCounter?: boolean };

export interface SliderProps {
    currentSlide: number;
    flexBasis: string;
    imageAltTitle?: string;
    isDialogOpen: boolean;
    itemsToShow: number;
    onClick?(...args: unknown[]): unknown;
    openLightbox?(...args: unknown[]): unknown;
    padding?: string;
    showFullScreenOnClick?: boolean;
    showNextSlidePreview?: boolean;
    slides: Slide[];
}

const Slider = ({
    slides,
    currentSlide,
    isDialogOpen,
    flexBasis,
    imageAltTitle,
    padding,
    openLightbox,
    showNextSlidePreview,
    itemsToShow,
    showFullScreenOnClick,
    onClick,
}: SliderProps) => {
    return (
        <>
            {slides.map((el, index) => {
                const isVideoElement = el.type && el.type === 'video';
                return (
                    <div
                        role="listitem"
                        data-testid="slider"
                        className={getClassName(
                            currentSlide,
                            index,
                            itemsToShow,
                            showNextSlidePreview,
                            slides.length,
                            isVideoElement
                        )}
                        style={{
                            flexBasis,
                            padding,
                            maxWidth: flexBasis,
                            width: flexBasis,
                        }}
                        onClick={(e) => {
                            if (onClick) {
                                return onClick(e, el);
                            }
                            if (showFullScreenOnClick) {
                                openLightbox && openLightbox(currentSlide);
                                trackV2('SliderFullScreen', 'image');
                            }
                        }}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`carousel-slider-${index}`}
                    >
                        <>
                            {el.type === 'image' && !isVideoElement && (
                                <img src={el.link} alt={imageAltTitle} />
                            )}
                            {el.type === 'html' && el.content}
                            {el.type === 'card' && (
                                <CarouselCard
                                    path={el.path}
                                    imageUrl={el.imageUrl}
                                    price={el.price}
                                    title={el.title}
                                    location={el.location}
                                    postedDate={el.postedDate}
                                    id={el.id}
                                />
                            )}
                            {!isDialogOpen &&
                                el.type !== 'display-ad' &&
                                !isVideoElement &&
                                slides.length > 1 && <div className="slide-overlay" />}
                            {isVideoElement && el.type === 'video' && (
                                <iframe
                                    css={videoStyles}
                                    width="320"
                                    height="320"
                                    title="VIP video"
                                    src={`${el.link}?mute=0`}
                                    frameBorder="0"
                                    allowFullScreen
                                />
                            )}
                            {el.type === 'display-ad' && (
                                <StyledAdContainer>
                                    <AdSlot id={el.slotId} />
                                </StyledAdContainer>
                            )}
                        </>
                    </div>
                );
            })}
        </>
    );
};

Slider.defaultProps = {
    imageAltTitle: '',
    openLightbox: () => {},
    onClick: null,
    showFullScreenOnClick: null,
    showNextSlidePreview: false,
};

export default Slider;
